import { useEffect, useState, memo } from 'react';
import * as React from 'react';
import styled from 'styled-components';
import Table from '@sdk/components/ui/table/Table';
import TableHeader from '@sdk/components/ui/table/TableHeader';
import TableHeaderCell from '@sdk/components/ui/table/TableHeaderCell';
import TableRow from '@sdk/components/ui/table/TableRow';
import TableCell from '@sdk/components/ui/table/TableCell';
import Checkbox from '@sdk/components/forms/Checkbox';
import Button from '@sdk/components/ui/buttons/Button';
import Href from '@sdk/components/ui/Href';
import Text14 from '@sdk/components/ui/typography/Text14';
import Row from '@sdk/components/ui/spacing/Row';
import useSlingApi from '@sdk/api/useSlingApi';
import useMounted from '@sdk/utils/hooks/useMounted';
import dateToHumanFormat from '@sdk/utils/dateFormat/dateToHumanFormat';
import getRealDate from '@sdk/utils/getRealDate';
import formatTime from '@sdk/utils/dateFormat/formatTime';
import gettext from '@sdk/lang/gettext';
import useModalState from '@sdk/components/ui/modal/useModalState';
import ConfirmModal from '@sdk/components/ui/confirmModal/ConfirmModal';
import WarningIconSvg from '@sdk/assets/svg/Icons/32/announcement-red.svg';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import { ButtonType } from '@sdk/components/ui/buttons/ButtonType';
import type { AuthUserType } from '@sdk/authentication/types/AuthUserType';
import type { KioskSession } from '@sdk/kiosk/types/apiTypes';

import type { $stateType } from '../../../common/types/AngularTypes';

import LocationsCell from './LocationsCell';
import EllipsisLink from './EllipsisLink';

/**
 * Setting that's responsible for displaying less row in Kiosk Sessions table until they are specifically required.
 */
const MAX_SESSIONS_ON_FIRST_RENDER = 5;

const FirstCell = styled(TableCell)`
  padding-left: 10px !important;
  padding-right: 10px !important;
`;

const FirstHeaderCell = styled(TableHeaderCell)`
  padding-left: 10px !important;
  padding-right: 10px !important;
`;

const Link = styled(Href)`
  font-size: ${({ theme }): string => theme.fontSizes.l};
  color: ${({ theme }): string => theme.colors.primary};
`;

const CheckboxLabel = styled(Href)`
  font-size: ${({ theme }): string => theme.fontSizes.l};
  color: ${({ theme }): string => theme.colors.iconGray};
  padding-left: 5px;
`;

const RowAlignedCenter = styled(Row)`
  align-items: center;
`;

type Props = {
  personasDict: {
    [id in string]: AuthUserType;
  };
  $state: $stateType;
};

const KioskSessionsTable: React.FC<React.PropsWithChildren<Props>> = ({ personasDict, $state }) => {
  const [loadSessionsError, setLoadSessionsError] = useState('');
  const [kioskSessions, setKioskSessions] = useState<KioskSession[]>([]);
  const [selectedSessions, setSelectedSessions] = useState({});
  const [allSessionsSelected, setAllSessionsSelected] = useState(false);
  /**
   * `hasSelection` is true if at least one session is selected.
   */
  const [hasSelection, setHasSelection] = useState(false);
  const [confirmSignOut, setConfirmSignOut] = useState(false);
  const [hiddenKioskSessions, setHiddenKioskSessions] = useState(0);
  const [openedKioskSessionId, setOpenedKioskSessionId] = useState(null);
  const mountedRef = useMounted();
  const api = useSlingApi();
  const { isVisible, open, close } = useModalState();
  const {
    isVisible: isMultiSessionModalVisible,
    open: multiSessionModalOpen,
    close: multiSessionModalClose,
  } = useModalState();

  useEffect(() => {
    (async (): Promise<void> => {
      try {
        const { data } = await api.kiosk.getSessions();
        setKioskSessions(data);
        if (data.length > MAX_SESSIONS_ON_FIRST_RENDER) {
          setHiddenKioskSessions(data.length - MAX_SESSIONS_ON_FIRST_RENDER);
        }
      } catch ({ data }) {
        if (mountedRef.current) {
          setLoadSessionsError(data.message);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleSelection = (kioskSessionId: number, isSelected: boolean): void => {
    const newSelection = {
      ...selectedSessions,
      [kioskSessionId]: isSelected,
    };

    setSelectedSessions(newSelection);
    const selectedCount = Object.keys(newSelection).reduce((acc, id) => {
      if (newSelection[id]) {
        acc = acc + 1;
      }
      return acc;
    }, 0);

    if (selectedCount === kioskSessions.length) {
      setAllSessionsSelected(true);
      setHasSelection(true);
    } else if (!selectedCount) {
      setAllSessionsSelected(false);
      setHasSelection(false);
    } else {
      setHasSelection(true);
    }
  };

  const toggleAllSessionsSelected = (allSelected: boolean): void => {
    if (!allSelected) {
      setSelectedSessions({});
      setHasSelection(false);
    } else {
      setSelectedSessions(
        kioskSessions.reduce((acc, kioskSession) => {
          acc[kioskSession.id] = true;
          return acc;
        }, {})
      );
      setHasSelection(true);
    }

    setAllSessionsSelected(allSelected);
  };

  const signOut = async (kioskSessionId: number): Promise<void> => {
    setOpenedKioskSessionId(kioskSessionId);
    open();
  };

  const onSignOutConfirm = async (): Promise<void> => {
    await api.kiosk.deleteSessions([openedKioskSessionId]);
    setKioskSessions(kioskSessions.filter(({ id }) => id !== openedKioskSessionId));
    setOpenedKioskSessionId(null);
    setConfirmSignOut(false);
  };

  const onSignOutCancel = (): void => {
    close();
    setConfirmSignOut(false);
    setOpenedKioskSessionId(null);
  };

  const toggleConfirmSignOut = (): void => {
    setConfirmSignOut(!confirmSignOut);
  };

  const onMultiSessionSignOutConfirm = async (): Promise<void> => {
    const kioskSessionIds = Object.keys(selectedSessions).reduce((acc, id) => {
      if (selectedSessions[id]) {
        acc.push(Number(id));
      }
      return acc;
    }, []);
    await api.kiosk.deleteSessions(kioskSessionIds);
    setKioskSessions(kioskSessions.filter(({ id }) => !kioskSessionIds.includes(id)));
    setConfirmSignOut(false);
  };

  const onMultiSessionSignOutCancel = (): void => {
    multiSessionModalClose();
    setConfirmSignOut(false);
  };

  const formatLastSeen = (lastSeen: string): string => {
    const date = getRealDate(lastSeen);
    return `${formatTime(date)}\n${dateToHumanFormat(date)}`;
  };

  const formatCreatedBy = (createdById: number): JSX.Element | string => {
    const user = personasDict[createdById];

    if (user) {
      return (
        <EllipsisLink
          onClick={(): void => {
            $state.go('users.profile', { userId: createdById, tab: 'profile' });
          }}>
          {user.fullname}
        </EllipsisLink>
      );
    }
    return '';
  };

  const getMultiSessionModalTitle = (): string => {
    const count = Object.keys(selectedSessions).reduce((result, id) => {
      if (selectedSessions[id]) {
        result = result + 1;
      }
      return result;
    }, 0);
    return gettext('Sign out %(count)s kiosk sessions', { count });
  };

  const showAllSessions = (): void => {
    setHiddenKioskSessions(0);
  };

  if (loadSessionsError) {
    return <Text14 themeColor={ThemeColorType.danger}>{loadSessionsError}</Text14>;
  }

  if (!kioskSessions.length) {
    return <Text14>{gettext('You do not have any Kiosk sessions')}</Text14>;
  }

  return (
    <>
      {hasSelection && (
        <Row mBottom={20}>
          <Link onClick={multiSessionModalOpen}>{gettext('Sign out selected sessions')}</Link>
        </Row>
      )}
      <Table>
        <TableHeader>
          <FirstHeaderCell label="" width={38}>
            <Checkbox value={allSessionsSelected} onChange={toggleAllSessionsSelected} />
          </FirstHeaderCell>
          <TableHeaderCell label={gettext('id')} width={38} />
          <TableHeaderCell label={gettext('created by')} />
          <TableHeaderCell label={gettext('locations')} />
          <TableHeaderCell label={gettext('ip address')} />
          <TableHeaderCell label={gettext('last active')} />
          <TableHeaderCell label="" width={90} />
        </TableHeader>
        <tbody>
          {kioskSessions?.map((kioskSession, index) => {
            // By default hide more then MAX_SESSIONS_ON_FIRST_RENDER sessions
            if (hiddenKioskSessions && index + 1 > MAX_SESSIONS_ON_FIRST_RENDER) {
              return null;
            }

            return (
              <TableRow key={kioskSession.id}>
                <FirstCell>
                  <Checkbox
                    value={selectedSessions[kioskSession.id]}
                    onChange={(isSelected: boolean): void =>
                      toggleSelection(kioskSession.id, isSelected)
                    }
                  />
                </FirstCell>
                <TableCell>{String(kioskSession.id)}</TableCell>
                <TableCell>{formatCreatedBy(kioskSession.creator.id)}</TableCell>
                <TableCell isColumn pRight={10}>
                  <LocationsCell locations={kioskSession.personas} $state={$state} />
                </TableCell>
                <TableCell>{kioskSession.ip}</TableCell>
                <TableCell>{formatLastSeen(kioskSession.lastseen)}</TableCell>
                <TableCell>
                  <Button
                    onClick={(): void => {
                      signOut(kioskSession.id);
                    }}>
                    {gettext('Sign out')}
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </tbody>
      </Table>
      {Boolean(hiddenKioskSessions) && (
        <Row mTop={20}>
          <Link onClick={showAllSessions}>
            {gettext('Show %(hiddenKioskSessions)s more', { hiddenKioskSessions })}
          </Link>
        </Row>
      )}
      {isVisible && (
        <ConfirmModal
          titleText={gettext('Sign out kiosk session')}
          titleIcon={<WarningIconSvg />}
          primaryButtonText={gettext('Sign out')}
          secondaryButtonText={gettext('Cancel')}
          titleThemeColor={ThemeColorType.danger}
          primaryButtonType={ButtonType.danger}
          dismiss={onSignOutCancel}
          onPrimaryClick={onSignOutConfirm}
          primaryButtonDisabled={!confirmSignOut}
          fixed>
          <Text14 bold themeColor={ThemeColorType.danger}>
            {gettext('Warning: this connot be undone.')}
          </Text14>
          <Text14>
            {gettext(
              'Please confirm that you understand what will happen by signing out this kiosk session:'
            )}
          </Text14>
          <RowAlignedCenter mTop={20}>
            <Checkbox value={confirmSignOut} onChange={setConfirmSignOut} />
            <CheckboxLabel onClick={toggleConfirmSignOut}>
              {gettext(
                'Signing out of the kiosk means employees will not be able to use it to clock in'
              )}
            </CheckboxLabel>
          </RowAlignedCenter>
        </ConfirmModal>
      )}
      {isMultiSessionModalVisible && (
        <ConfirmModal
          titleText={getMultiSessionModalTitle()}
          titleIcon={<WarningIconSvg />}
          primaryButtonText={gettext('Sign out')}
          secondaryButtonText={gettext('Cancel')}
          titleThemeColor={ThemeColorType.danger}
          primaryButtonType={ButtonType.danger}
          dismiss={onMultiSessionSignOutCancel}
          onPrimaryClick={onMultiSessionSignOutConfirm}
          primaryButtonDisabled={!confirmSignOut}
          fixed>
          <Text14 bold themeColor={ThemeColorType.danger}>
            {gettext('Warning: this connot be undone.')}
          </Text14>
          <Text14>
            {gettext(
              'Please confirm that you understand what will happen by signing out this kiosk session:'
            )}
          </Text14>
          <RowAlignedCenter mTop={20}>
            <Checkbox value={confirmSignOut} onChange={setConfirmSignOut} />
            <CheckboxLabel onClick={toggleConfirmSignOut}>
              {gettext(
                'Signing out of the kiosk means employees will not be able to use it to clock in'
              )}
            </CheckboxLabel>
          </RowAlignedCenter>
        </ConfirmModal>
      )}
    </>
  );
};

export default memo(KioskSessionsTable);
